import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { isAxiosError } from 'axios';
import { inventoryService } from '@/services';
import { queryKeys } from '@/services/query-keys';
import { InventoryState } from '@/types';
import { useInventoryDataStore, useInventoryFilterStore } from '@/stores';
import { logger } from '@/utils/logger';
import { getDefaultStateId, isPinnedToFollowedState } from '@/utils/inventory/intraday-state-utils';
import { getStoredFollowedStateId, setStoredFollowedStateId, clearStoredFollowedStateId } from '@/utils/inventory/force-collect-storage';

const POLL_INTERVAL_MS = 5_000;
// Safety fallback: must be >= backend once_only_task lock TTL
// (INVENTORY_COLLECT_LOCK_TTL_SECONDS = 900s) + at least one poll interval so
// we can observe the final is_running=false after the lock expires. Keep in
// sync with MAX_AGE_MS in force-collect-storage.ts.
const MAX_POLLING_DURATION_MS = 960_000; // 16 minutes
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

interface UseForceInventoryCollectionOptions {
  onNewDataDetected?: (newStateId: number) => void;
}

export const useForceInventoryCollection = (options?: UseForceInventoryCollectionOptions) => {
  const queryClient = useQueryClient();
  const setIsForceCollecting = useInventoryDataStore((state) => state.setIsForceCollecting);
  const setIsForceCollectSuccess = useInventoryDataStore((state) => state.setIsForceCollectSuccess);
  const setForceCollectionTaskId = useInventoryDataStore((state) => state.setForceCollectionTaskId);
  const inventoryFilters = useInventoryFilterStore((state) => state.inventoryFilters);
  const setInventoryFilters = useInventoryFilterStore((state) => state.setInventoryFilters);
  const lastFollowedStateIdRef = useRef<number | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);
  const pollErrorCountRef = useRef<number>(0);
  const hasRestoredRef = useRef<boolean>(false);
  const { onNewDataDetected } = options || {};

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollingStartTimeRef.current = null;
  }, []);

  const clearSuccessTimeout = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const resetForceCollectState = useCallback(() => {
    stopPolling();
    setIsForceCollecting(false);
    setForceCollectionTaskId(null);
    clearStoredFollowedStateId();
  }, [stopPolling, setIsForceCollecting, setForceCollectionTaskId]);

  const handleNewStateDetected = useCallback((newStateId: number) => {
    resetForceCollectState();

    // Same follow predicate as the 45s polling hook: only an explicit pin to the
    // former-newest id is bumped; the default view (undefined) auto-follows via
    // InventoryPage's `inventoryFilters.state || defaultStateId` (LR-503).
    if (isPinnedToFollowedState(inventoryFilters.state, lastFollowedStateIdRef.current)) {
      setInventoryFilters({ ...inventoryFilters, state: newStateId });
    }

    queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all] });

    clearSuccessTimeout();
    setIsForceCollectSuccess(true);
    successTimeoutRef.current = setTimeout(() => {
      setIsForceCollectSuccess(false);
      successTimeoutRef.current = null;
    }, 5000);

    if (onNewDataDetected) {
      onNewDataDetected(newStateId);
    }
    lastFollowedStateIdRef.current = newStateId;
  }, [resetForceCollectState, inventoryFilters, setInventoryFilters, queryClient, clearSuccessTimeout, setIsForceCollectSuccess, onNewDataDetected]);

  const handleCollectionFinished = useCallback(async () => {
    try {
      const states = await inventoryService.getInventoryStates();
      queryClient.setQueryData(queryKeys.inventory.states(), states);
      // Force-collect always creates a fresh intraday (LR-396) which becomes the
      // newest state, so a changed newest id means a new snapshot appeared (LR-503).
      const currentNewestId = getDefaultStateId(states);

      if (currentNewestId !== undefined && currentNewestId !== lastFollowedStateIdRef.current) {
        handleNewStateDetected(currentNewestId);
      } else {
        // Task finished but no new state was created
        logger.warn('Force collection completed but no new inventory state was created');
        resetForceCollectState();
        queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all] });
      }
    } catch (error) {
      logger.error('Error checking inventory states after collection:', error);
      resetForceCollectState();
    }
  }, [queryClient, handleNewStateDetected, resetForceCollectState]);

  const schedulePoll = useCallback(() => {
    pollingIntervalRef.current = setTimeout(async () => {
      // Safety timeout fallback
      if (pollingStartTimeRef.current && Date.now() - pollingStartTimeRef.current > MAX_POLLING_DURATION_MS) {
        logger.warn('Force collection polling timed out after 2 minutes');
        resetForceCollectState();
        queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all] });
        return;
      }

      try {
        const { is_running } = await inventoryService.getCollectionInfo();
        pollErrorCountRef.current = 0;

        if (!is_running) {
          // Task finished — check if a new state appeared
          await handleCollectionFinished();
        } else {
          schedulePoll();
        }
      } catch (error) {
        pollErrorCountRef.current += 1;
        logger.error(
          `Error polling collection status (attempt ${pollErrorCountRef.current}/${MAX_CONSECUTIVE_POLL_ERRORS}):`,
          error,
        );
        if (pollErrorCountRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
          logger.warn('Force collection polling aborted after consecutive errors');
          resetForceCollectState();
          queryClient.invalidateQueries({ queryKey: [...queryKeys.inventory.all] });
          return;
        }
        schedulePoll();
      }
    }, POLL_INTERVAL_MS);
  }, [queryClient, handleCollectionFinished, resetForceCollectState]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingStartTimeRef.current = Date.now();
    pollErrorCountRef.current = 0;
    schedulePoll();
  }, [stopPolling, schedulePoll]);

  const enterPollingMode = useCallback(() => {
    clearSuccessTimeout();
    setIsForceCollectSuccess(false);
    setIsForceCollecting(true);

    const currentStates = queryClient.getQueryData<InventoryState[]>(queryKeys.inventory.states());
    if (currentStates && currentStates.length > 0) {
      const newestStateId = getDefaultStateId(currentStates);
      if (newestStateId !== undefined) {
        lastFollowedStateIdRef.current = newestStateId;
        setStoredFollowedStateId(newestStateId);
      }
    }
    startPolling();
  }, [clearSuccessTimeout, setIsForceCollectSuccess, setIsForceCollecting, queryClient, startPolling]);

  const mutation = useMutation({
    mutationFn: () => inventoryService.forceCollectInventory(),
    onSuccess: (data) => {
      setForceCollectionTaskId(data.task_id);
      enterPollingMode();
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 409) {
        enterPollingMode();
      } else {
        clearSuccessTimeout();
        setIsForceCollecting(false);
        setIsForceCollectSuccess(false);
        setForceCollectionTaskId(null);
        clearStoredFollowedStateId();
      }
    },
  });

  useEffect(() => {
    return () => {
      stopPolling();
      clearSuccessTimeout();
    };
  }, [stopPolling, clearSuccessTimeout]);

  // Restore polling state from localStorage on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const savedId = getStoredFollowedStateId();
    if (savedId !== null && !pollingIntervalRef.current) {
      lastFollowedStateIdRef.current = savedId;
      setIsForceCollecting(true);
      startPolling();
    }
  }, [setIsForceCollecting, startPolling]);

  return {
    ...mutation,
    isPolling: pollingIntervalRef.current !== null,
  };
};
